import { combineWatchers, lens, meta, watch } from "@dhmk/zustand-lens";
import type {
  ChatOnDataCallback,
  ChatOnErrorCallback,
  ChatOnFinishCallback,
} from "ai";
import type {
  ImagineUIDataParts,
  ImagineUIMessage,
} from "@imagine/ai-service/shared-types";
import { Subject, BehaviorSubject } from "rxjs";
import type { UseChatHelpers } from "@ai-sdk/react";

type ChatOnFinishCallbackArg = Parameters<
  ChatOnFinishCallback<ImagineUIMessage>
>[0];
type ChatOnDataCallbackArg = Parameters<
  ChatOnDataCallback<ImagineUIMessage>
>[0];
type ChatOnErrorCallbackArg = Parameters<ChatOnErrorCallback>[0];

export type AISDKChat = UseChatHelpers<ImagineUIMessage>;

type ChatState = {
  onData$: Subject<ChatOnDataCallbackArg>;
  onFinish$: Subject<ChatOnFinishCallbackArg>;
  onError$: Subject<ChatOnErrorCallbackArg>;
  status: AISDKChat["status"];
  isGenerating: boolean;
  todos: ImagineUIDataParts["plan"]["items"];
  scrollToBottom$: Subject<void>;
  scrollLocked$: BehaviorSubject<boolean>;
  history: Array<ImagineUIMessage> | null;
  submittedUserMessage: ImagineUIMessage | null;
  currentAssistantMessage: ImagineUIMessage | null;
  error: Error | undefined;
};

type ChatActions = {
  reset: () => void;
  setStatus: (status: AISDKChat["status"]) => void;
  setTodos: (todos: ImagineUIDataParts["plan"]["items"]) => void;
  resetTodos: () => void;
  setHistory: (history: Array<ImagineUIMessage> | null) => void;
  syncHistoryAfterGeneration: () => void;
  setSubmittedUserMessage: (
    submittedUserMessage: ImagineUIMessage | null,
  ) => void;
  setCurrentAssistantMessage: (
    currentAssistantMessage: ImagineUIMessage | null,
  ) => void;
  setError: (error: Error | undefined) => void;
  lockScroll: () => void;
  unlockScroll: () => void;
};

export type ChatSlice = ChatState & ChatActions;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const defaultChatState: ChatState = {
  onData$: new Subject<ChatOnDataCallbackArg>(),
  onFinish$: new Subject<ChatOnFinishCallbackArg>(),
  onError$: new Subject<ChatOnErrorCallbackArg>(),
  status: "ready",
  todos: [],
  scrollToBottom$: new Subject<void>(),
  scrollLocked$: new BehaviorSubject<boolean>(false),
  isGenerating: false,
  history: null,
  submittedUserMessage: null,
  currentAssistantMessage: null,
  error: undefined,
};

export const chatSlice = lens<ChatSlice>((set, get) => ({
  ...defaultChatState,
  setStatus: (status) => set({ status }),
  setHistory: (history) => {
    console.log("setHistory", history);
    set({ history, currentAssistantMessage: null, submittedUserMessage: null });
  },
  syncHistoryAfterGeneration: () => {
    const currentAssistantMessage = get().currentAssistantMessage;
    const submittedUserMessage = get().submittedUserMessage;
    const currentHistory = get().history ?? [];

    const newHistory = [...currentHistory];

    if (submittedUserMessage) {
      newHistory.push(submittedUserMessage);
    }

    if (currentAssistantMessage) {
      newHistory.push(currentAssistantMessage);
    }

    set({
      history: newHistory,
      currentAssistantMessage: null,
      submittedUserMessage: null,
    });
  },
  setSubmittedUserMessage: (submittedUserMessage) => {
    set({ submittedUserMessage });
  },
  setCurrentAssistantMessage: (currentAssistantMessage) => {
    set({ currentAssistantMessage });
  },
  setError: (error) => set({ error }),
  setTodos: async (todos) => {
    // First, we always set the todos
    set({ todos });

    // We want to check if all todos are resolved
    const allTodosResolved = todos.every(
      (todo) =>
        todo.status === "completed" ||
        todo.status === "skipped" ||
        todo.status === "failed",
    );

    // Then we wait, and reset the todos. This makes sure the user has time to see all resolved todos before hiding them
    if (allTodosResolved) {
      await wait(2000);
      get().resetTodos();
    }
  },
  resetTodos: () => set({ todos: [] }),
  lockScroll: () => {
    get().scrollLocked$.next(true);
  },
  unlockScroll: () => {
    get().scrollLocked$.next(false);
  },
  reset: () => {
    set({ ...defaultChatState });
  },
  [meta]: {
    setter: combineWatchers(
      watch(
        (state) => state.status,
        (value) => {
          set({ isGenerating: value === "submitted" || value === "streaming" });
          return value;
        },
      ),
      watch(
        (state) => ({
          history: state.history,
          submittedUserMessage: state.submittedUserMessage,
          currentAssistantMessage: state.currentAssistantMessage,
        }),
        ({ history, submittedUserMessage, currentAssistantMessage }) => {
          get().scrollToBottom$.next();
          return {
            history,
            submittedUserMessage,
            currentAssistantMessage,
          };
        },
      ),
    ),
  },
}));
